import { useState, useRef, useEffect } from 'react'
import './CanvasPanControls.css'

interface CanvasPanControlsProps {
  onPan?: (deltaX: number, deltaY: number) => void
  onZoom?: (scaleFactor: number) => void
}

export function CanvasPanControls({ onPan, onZoom }: CanvasPanControlsProps) {
  const [pixelValue, setPixelValue] = useState(10)
  const [isLongPressing, setIsLongPressing] = useState(false)
  const [currentDirection, setCurrentDirection] = useState<'up' | 'down' | 'left' | 'right' | 'zplus' | 'zminus' | null>(null)
  const longPressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Continuous pan interval - triggered when long pressing changes
  useEffect(() => {
    if (!isLongPressing || !currentDirection) {
      return
    }

    const delta = pixelValue

    // Pan every 50ms while long pressing
    const intervalId = setInterval(() => {
      switch (currentDirection) {
        case 'up':
          onPan?.(0, delta)
          break
        case 'down':
          onPan?.(0, -delta)
          break
        case 'left':
          onPan?.(delta, 0)
          break
        case 'right':
          onPan?.(-delta, 0)
          break
        case 'zplus':
          // Zoom in by 10% each interval
          onZoom?.(1.1)
          break
        case 'zminus':
          // Zoom out by 10% each interval
          onZoom?.(0.9)
          break
      }
    }, 50)

    return () => clearInterval(intervalId)
  }, [isLongPressing, currentDirection, pixelValue, onPan, onZoom])

  const handleMouseDown = (direction: 'up' | 'down' | 'left' | 'right' | 'zplus' | 'zminus') => {
    setCurrentDirection(direction)

    // Start long press timer (300ms before continuous pan starts)
    longPressTimerRef.current = setTimeout(() => {
      setIsLongPressing(true)
    }, 300)
  }

  const handleMouseUp = () => {
    // Cancel long press
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current)
      longPressTimerRef.current = null
    }

    // Single click if not long pressed
    if (!isLongPressing && currentDirection) {
      const delta = pixelValue

      switch (currentDirection) {
        case 'up':
          onPan?.(0, delta)
          break
        case 'down':
          onPan?.(0, -delta)
          break
        case 'left':
          onPan?.(delta, 0)
          break
        case 'right':
          onPan?.(-delta, 0)
          break
        case 'zplus':
          // Zoom in by 10%
          onZoom?.(1.1)
          break
        case 'zminus':
          // Zoom out by 10%
          onZoom?.(0.9)
          break
      }
    }

    // Reset state
    setIsLongPressing(false)
    setCurrentDirection(null)
  }

  return (
    <div className="canvas-pan-controls">
      {/* Z+ Button (above right, left of up) */}
      <button
        className="pan-button pan-zplus"
        onMouseDown={() => handleMouseDown('zplus')}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        title="Pan Z+ (click or hold)"
      >
        Z+
      </button>

      {/* Up Arrow */}
      <button
        className="pan-button pan-up"
        onMouseDown={() => handleMouseDown('up')}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        title="Pan up (click or hold)"
      >
        ↑
      </button>

      {/* Left Arrow */}
      <button
        className="pan-button pan-left"
        onMouseDown={() => handleMouseDown('left')}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        title="Pan left (click or hold)"
      >
        ←
      </button>

      {/* Center Input Box */}
      <div className="pan-center">
        <input
          type="number"
          value={pixelValue}
          onChange={(e) => setPixelValue(Math.max(1, parseInt(e.target.value) || 1))}
          className="pan-pixel-input"
          title="Pixel value for each arrow click"
        />
        <span className="pan-pixel-label">px</span>
      </div>

      {/* Right Arrow */}
      <button
        className="pan-button pan-right"
        onMouseDown={() => handleMouseDown('right')}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        title="Pan right (click or hold)"
      >
        →
      </button>

      {/* Z- Button (below left, left of down) */}
      <button
        className="pan-button pan-zminus"
        onMouseDown={() => handleMouseDown('zminus')}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        title="Pan Z- (click or hold)"
      >
        Z-
      </button>

      {/* Down Arrow */}
      <button
        className="pan-button pan-down"
        onMouseDown={() => handleMouseDown('down')}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        title="Pan down (click or hold)"
      >
        ↓
      </button>
    </div>
  )
}
